import { createContext, useContext, useState, useEffect, useCallback } from 'react';

const CURRENCIES = {
  IN: { code: 'INR', symbol: '₹', locale: 'en-IN', flag: '🇮🇳', label: 'India' },
  US: { code: 'USD', symbol: '$', locale: 'en-US', flag: '🇺🇸', label: 'United States' },
  GB: { code: 'GBP', symbol: '£', locale: 'en-GB', flag: '🇬🇧', label: 'United Kingdom' },
  EU: { code: 'EUR', symbol: '€', locale: 'de-DE', flag: '🇪🇺', label: 'Europe' },
  AE: { code: 'AED', symbol: 'د.إ', locale: 'ar-AE', flag: '🇦🇪', label: 'UAE' },
  CA: { code: 'CAD', symbol: 'C$', locale: 'en-CA', flag: '🇨🇦', label: 'Canada' },
  AU: { code: 'AUD', symbol: 'A$', locale: 'en-AU', flag: '🇦🇺', label: 'Australia' },
  SG: { code: 'SGD', symbol: 'S$', locale: 'en-SG', flag: '🇸🇬', label: 'Singapore' },
  JP: { code: 'JPY', symbol: '¥', locale: 'ja-JP', flag: '🇯🇵', label: 'Japan' },
};

// Approximate conversion rates from INR (base)
const RATES_FROM_INR = {
  INR: 1,
  USD: 0.012,
  GBP: 0.0095,
  EUR: 0.011,
  AED: 0.044,
  CAD: 0.016,
  AUD: 0.018,
  SGD: 0.016,
  JPY: 1.78,
};

const CurrencyContext = createContext(null);

export const CurrencyProvider = ({ children }) => {
  const [country, setCountry] = useState(() => localStorage.getItem('ainaa_country') || 'IN');
  const [lang, setLang] = useState(() => localStorage.getItem('ainaa_lang') || 'en');

  const currency = CURRENCIES[country] || CURRENCIES.IN;

  // Format INR amount to selected currency
  const formatAmount = useCallback((inrAmount, { compact = false, decimals = 0 } = {}) => {
    const rate = RATES_FROM_INR[currency.code] || 1;
    const converted = inrAmount * rate;

    if (compact) {
      if (currency.code === 'INR') {
        if (converted >= 10000000) return `${currency.symbol}${(converted / 10000000).toFixed(1)} Cr`;
        if (converted >= 100000) return `${currency.symbol}${(converted / 100000).toFixed(1)} L`;
      } else if (currency.code === 'JPY') {
        if (converted >= 100000000) return `${currency.symbol}${(converted / 100000000).toFixed(1)}B`;
        if (converted >= 1000000) return `${currency.symbol}${(converted / 1000000).toFixed(1)}M`;
      } else {
        if (converted >= 1000000000) return `${currency.symbol}${(converted / 1000000000).toFixed(1)}B`;
        if (converted >= 1000000) return `${currency.symbol}${(converted / 1000000).toFixed(1)}M`;
        if (converted >= 1000) return `${currency.symbol}${(converted / 1000).toFixed(1)}K`;
      }
    }
    return new Intl.NumberFormat(currency.locale, {
      style: 'currency',
      currency: currency.code,
      maximumFractionDigits: decimals,
    }).format(converted);
  }, [currency]);

  const updateCountry = useCallback((c) => {
    setCountry(c);
    localStorage.setItem('ainaa_country', c);
  }, []);

  const updateLang = useCallback((l) => {
    setLang(l);
    localStorage.setItem('ainaa_lang', l);
  }, []);

  // Listen for external changes (footer selector)
  useEffect(() => {
    const handler = () => {
      const stored = localStorage.getItem('ainaa_country');
      if (stored && stored !== country) setCountry(stored);
    };
    window.addEventListener('ainaa-country-change', handler);
    return () => window.removeEventListener('ainaa-country-change', handler);
  }, [country]);

  useEffect(() => {
    const handler = () => {
      const stored = localStorage.getItem('ainaa_lang');
      if (stored && stored !== lang) setLang(stored);
    };
    window.addEventListener('ainaa-lang-change', handler);
    return () => window.removeEventListener('ainaa-lang-change', handler);
  }, [lang]);

  return (
    <CurrencyContext.Provider value={{ country, currency, lang, formatAmount, updateCountry, updateLang, CURRENCIES }}>
      {children}
    </CurrencyContext.Provider>
  );
};

export const useCurrency = () => {
  const ctx = useContext(CurrencyContext);
  if (!ctx) throw new Error('useCurrency must be inside CurrencyProvider');
  return ctx;
};

export { CURRENCIES };
